import * as React from "react";
import {defineMessages, FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {Product} from "../../products/model";
import {actions} from "../actions";
import {Service} from "../model";
import {selectors as serviceSelectors} from "../selectors";
import {FeatureType, PublishedProduct} from "./model";
import {
  DetailViewListField,
  ReactElementWithValueChangeFunction,
  WfsPublishInfoDetailsSection
} from "./WfsPublishInfoDetailsSection";
import ValidatedTextBox, {Validation} from "./ValidatedTextBox";
import {WFS_PUBLISH_INFO_TABLE_ROW_HEIGHT, WfsPublishInfoTable} from "./WfsPublishInfoTable";

const FEATURE_TYPE_DETAILS_FIELD_VALIDATION_MESSAGES = defineMessages({
  whitespaceCharactersProhibited: {
    id: "studio.services.wfs.publish-info.feature-type-details-field.whiteSpaceCharacter.validation.message",
    defaultMessage: "{field} cannot be empty or whitespace",
  },
  emojiCharactersProhibited: {
    id: "studio.services.wfs.publish-info.feature-type-details-field.emojiCharacter.validation.message",
    defaultMessage: "{field} cannot contain emojis or special Unicode characters",
  }
});

export const FEATURE_TYPE_DETAILS_FIELD_UPDATE_STATUS_MESSAGES = defineMessages({
  success: {
    id: "studio.services.wfs.publish-info.feature-type-details-field.update-success.message",
    defaultMessage: "{field} changed successfully",
  },
  failure: {
    id: "studio.services.wfs.publish-info.feature-type-details-field.update-failure.message",
    defaultMessage: "{field} update failed with error message: {errorMessage}",
  }
});

export const WFS_PUBLISH_INFO_MESSAGES = defineMessages({
  tableNote: {
    id: "studio.UI.services.wfs.publish-info.table.note",
    defaultMessage: "* Select a row to show and update Feature Type details",
  }
});

export interface PublishedWfsFeatureTypeModel {
  productId: string;
  productName: string;
  featureTypeId: string;
  fileName: string;
  dataId: string;
  modelName: string;
  featureTypeName: string;
  title: string;
  xmlNamespace: string;
  xmlNamespacePrefix: string;
}

interface WfsPublishInfoProps {
  isLoaded: boolean;
  service: Service;
  serviceProducts: Product[];
  servicePublishedProducts: PublishedProduct[];
  loadServicePublishedProducts: () => any;
  selectedModelData: PublishedWfsFeatureTypeModel | null;
  selectedFeatureTypeChanged: (updatedWfsFeatureType: PublishedWfsFeatureTypeModel | null) => void;
}

interface WfsPublishInfoState {
  tableModelData: PublishedWfsFeatureTypeModel[];
}

export interface EntityProperty<T> {
  key: Extract<keyof T, string>;
  name: string;
}

interface EntityMappedTextEditProps<T> {
  entity: T
  property: EntityProperty<T>;
  value: string;
  intl?: InjectedIntl;
  onValueChange?: (entity: T, newValue: string) => Promise<Validation>;
}

type WfsPublishInfoComponentProps = WfsPublishInfoProps & WithApiProperties & InjectedIntlProps;

class EntityMappedTextEditComponent<T> extends React.Component<EntityMappedTextEditProps<T>, {}> {
  constructor(props: EntityMappedTextEditProps<T>) {
    super(props);
  }

  render() {
    const {entity, value, property, intl} = this.props;

    const handleEntityValueChange = (newValue: string) => {
      const {onValueChange} = this.props;
      if (onValueChange) {
        return onValueChange(entity, newValue);
      }
    }

    const inputTextValidator = (inputValue: string, fieldName: string): Validation => {
      // Skip validation for empty input value if the field is permitted to be empty
      if (property.key !== 'xmlNamespacePrefix') {
        if (!inputValue.trim()) {
          return {
            state: "error",
            message: intl.formatMessage(FEATURE_TYPE_DETAILS_FIELD_VALIDATION_MESSAGES.whitespaceCharactersProhibited,
                {field: fieldName})
          };
        }
      }

      // Regex to detect Unicode/emoji characters
      const unicodeEmojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
      if (unicodeEmojiRegex.test(inputValue)) {
        return {
          state: "error",
          message: intl.formatMessage(FEATURE_TYPE_DETAILS_FIELD_VALIDATION_MESSAGES.emojiCharactersProhibited,
              {field: fieldName})
        };
      }

      return {state: null, message: null};
    };

    return (
        <ValidatedTextBox key={`text_box_${property.key}`}
                          fieldName={property.name}
                          value={value}
                          className="type-name-validated-text-box"
                          onSubmit={handleEntityValueChange}
                          clientValidation={inputTextValidator}/>
    );
  }
}

class WfsPublishInfoComponent extends React.Component<WfsPublishInfoComponentProps, WfsPublishInfoState> {

  constructor(props: WfsPublishInfoComponentProps) {
    super(props);
    this.state = {
      tableModelData: [],
    };
  }

  componentDidMount() {
    const {isLoaded, loadServicePublishedProducts, selectedFeatureTypeChanged} = this.props;
    if (isLoaded) {
      loadServicePublishedProducts();
      selectedFeatureTypeChanged(null);
    }
  }

  componentDidUpdate(prevProps: WfsPublishInfoComponentProps) {
    if (prevProps.isLoaded !== this.props.isLoaded && this.props.isLoaded) {
      this.props.loadServicePublishedProducts();
    }

    if ((prevProps.servicePublishedProducts !== this.props.servicePublishedProducts ||
         prevProps.serviceProducts !== this.props.serviceProducts) &&
        this.props.servicePublishedProducts && this.props.serviceProducts) {
      const tableModelData: PublishedWfsFeatureTypeModel[] = [];
      if (this.props.serviceProducts?.length > 0) {
        for (const publishedProduct of this.props.servicePublishedProducts) {
          for (const featureType of publishedProduct.publishInfo.featureTypes) {
            if (this.props.serviceProducts.find(product => product.id === publishedProduct.id)) {
              const fileName = getFileName(featureType.dataId, publishedProduct.id, this.props.serviceProducts);
              if (fileName) {
                tableModelData.push({
                  productId: publishedProduct.id,
                  productName: publishedProduct.name,
                  fileName,
                  featureTypeId: featureType.id,
                  dataId: featureType.dataId,
                  modelName: featureType.modelName,
                  featureTypeName: featureType.featureTypeName,
                  title: featureType.title,
                  xmlNamespace: featureType.xmlNamespace,
                  xmlNamespacePrefix: featureType.xmlNamespacePrefix ?? ""
                });
              }
            }
          }
        }
      }

      if (!this.props.isLoaded) {
        if (this.props.selectedModelData) {
          this.props.selectedFeatureTypeChanged(null);
        }
      } else {
        this.setState({tableModelData});
      }
    }
  }

  componentWillUnmount() {
    this.resetPublishInformationState();
  }

  onTableRowClick = (selectedModelDataEntry: PublishedWfsFeatureTypeModel[], _selectedIndices: number[]) => {
    this.props.selectedFeatureTypeChanged(selectedModelDataEntry[0]);
  }

  resetPublishInformationState = () => {
    this.setState({tableModelData: []});
    this.props.selectedFeatureTypeChanged(null);
  }

  onFeatureTypePropertyChange = async (property: EntityProperty<FeatureType>,
                                       updatedModelDataSelection: PublishedWfsFeatureTypeModel,
                                       intl: InjectedIntl): Promise<Validation> => {
    const newTableModelData: PublishedWfsFeatureTypeModel[] = Object.assign(
        [] as PublishedWfsFeatureTypeModel[],
        this.state.tableModelData);
    const index = newTableModelData.findIndex(model => model.featureTypeId === updatedModelDataSelection.featureTypeId);
    if (index !== -1) {
      newTableModelData[index] = updatedModelDataSelection;
    }
    this.setState({tableModelData: newTableModelData});
    this.props.selectedFeatureTypeChanged(updatedModelDataSelection);
    return {
      state: "success",
      message: intl.formatMessage(FEATURE_TYPE_DETAILS_FIELD_UPDATE_STATUS_MESSAGES.success, {field: property.name})
    };
  }

  render() {
    const {
      service,
      servicePublishedProducts,
      selectedModelData,
      isLoaded,
      loadServicePublishedProducts,
      intl
    } = this.props;
    const {tableModelData} = this.state;
    if (!service || !servicePublishedProducts) {
      return null;
    }

    const fields: DetailViewListField<FeatureType>[] = [
      {
        property: {key: "title", name: "Title"},
        name: <FormattedMessage id="studio.service.publishInfo-page.feature-type.title"
                                defaultMessage="Title"/>,
        value: createTextElement(selectedModelData, {key: 'title', name: 'Title'},
            (publishedProductTableModel) => publishedProductTableModel.title, intl),
        onValueChange: this.onFeatureTypePropertyChange
      },
      {
        property: {key: 'featureTypeName', name: 'Name'},
        name: <FormattedMessage id="studio.service.publishInfo-page.feature-type.name"
                                defaultMessage="Name"/>,
        value: createTextElement(selectedModelData, {key: 'featureTypeName', name: 'Name'},
            (publishedProductTableModel) => publishedProductTableModel.featureTypeName, intl),
        onValueChange: this.onFeatureTypePropertyChange
      },
      {
        property: {key: 'xmlNamespace', name: 'Namespace'},
        name: <FormattedMessage id="studio.service.publishInfo-page.feature-type.xmlNamespace"
                                defaultMessage="Namespace"/>,
        value: createTextElement(selectedModelData, {key: 'xmlNamespace', name: 'Namespace'},
            (publishedProductTableModel) => publishedProductTableModel.xmlNamespace, intl),
        onValueChange: this.onFeatureTypePropertyChange
      },
      {
        property: {key: 'xmlNamespacePrefix', name: 'Prefix'},
        name: <FormattedMessage id="studio.service.publishInfo-page.feature-type.xmlNamespacePrefix"
                                defaultMessage="Prefix"/>,
        value: createTextElement(selectedModelData, {key: 'xmlNamespacePrefix', name: 'Prefix'},
            (publishedProductTableModel) => publishedProductTableModel.xmlNamespacePrefix, intl),
        onValueChange: this.onFeatureTypePropertyChange
      }
    ];

    return (
        <>
          {isLoaded && (
              <>
                <div> {/* This empty div is necessary! it's used by ControlRoomTable to calculate its dimensions.*/}
                  <WfsPublishInfoTable key={"studio.service.wfsPublishInfoTable"}
                                       data={tableModelData}
                                       onRowSelect={this.onTableRowClick}
                                       rowHeight={WFS_PUBLISH_INFO_TABLE_ROW_HEIGHT}
                                       canMultiSelect={false}
                                       isPaged={false}
                                       isAllDataLoaded={true}
                                       intl={intl}/>
                  <div className="TableNote" style={{marginTop: "10px"}}>
                    <span>{intl.formatMessage(WFS_PUBLISH_INFO_MESSAGES.tableNote)}</span>
                  </div>
                </div>
                {selectedModelData ?
                 <WfsPublishInfoDetailsSection key={"studio.service.publishInformationDetails"}
                                               service={service}
                                               data={selectedModelData}
                                               loadServicePublishedProducts={loadServicePublishedProducts}
                                               fields={fields}/> : <></>
                }
              </>
          )}
        </>
    );
  }
}

const createTextElement = (
    entity: PublishedWfsFeatureTypeModel,
    property: EntityProperty<PublishedWfsFeatureTypeModel>,
    getValue: (entity: PublishedWfsFeatureTypeModel) => string,
    intl?: InjectedIntl): ReactElementWithValueChangeFunction<PublishedWfsFeatureTypeModel> => {
  return (entity ?
          <EntityMappedTextEditComponent property={property}
                                         entity={entity}
                                         value={getValue(entity)}
                                         intl={intl}/> : <></>
  );
}

const getFileName = (dataId: string, productId: string, serviceProducts: Product[]): string | undefined => {
  return (serviceProducts?.find(product => product?.id === productId)?.contents?.find(
      content => (content?.data as any)?.publicId === dataId) as any)?.data?.entryPointFile?.fileName;
}

const mapStateToProps = (state, ownProps) => {
  const serviceId = ownProps.service.id; //injected by parent
  return {
    servicePublishedProducts: serviceSelectors.getServicePublishedProducts(state, serviceId),
    selectedModelData: serviceSelectors.getSelectedWfsFeatureType(state, serviceId),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const serviceId = ownProps.service.id; //injected by parent
  const productIds: string[] = ownProps.serviceProducts.map((product: Product) => product.id).reverse();

  return {
    loadServicePublishedProducts: () => dispatch(actions.loadServicePublishedProducts(serviceId, productIds)),
    selectedFeatureTypeChanged: (updatedWfsFeatureType: PublishedWfsFeatureTypeModel | null) => {
      dispatch(actions.selectedFeatureTypeChanged(serviceId, updatedWfsFeatureType));
    }
  };
};

export const PublishInformationComponent = WithApi(WfsPublishInfoComponent);

export const WfsPublishInfo = injectIntl(
    connect(mapStateToProps, mapDispatchToProps)(PublishInformationComponent));
